// components/portal/panelSection.tsx
'use client';

import React from 'react';

// The one section + row idiom of the Source right bar. It was written three times (the shortlist's
// stats rows, its Doctrine/Scoring cards, the selected brand's Scoring card) and was about to be
// written a fourth for the brand's enrich list, which is how the same bar ends up with four
// slightly different greys. It lives here once, next to PANEL_BUTTON, for the same reason.
//
// Sections are flat: a title, its figure, its rows. The bar used to box every section in its own
// grey card, which read as clutter next to the flat stats above them (2026-07-27 request).
//
// Every type size is pinned inline: globals.css forces div/span/label to 12px outside any layer,
// so a text-[..] utility loses whatever its specificity.

interface PanelSectionProps {
  /** Section name, left. */
  title: string;
  /** The section's own figure, right (a total, a count). Omitted when there is nothing to state. */
  aside?: React.ReactNode;
  children?: React.ReactNode;
}

export const PanelSection = ({ title, aside, children }: PanelSectionProps) => (
  // No rule between sections: space separates them, as in ChatGPT's settings (owner, 2026-09-28).
  <section className="pt-4">
    <div className="flex items-center justify-between gap-2">
      <div className="font-medium text-white" style={{ fontSize: 14 }}>
        {title}
      </div>
      {aside !== undefined && aside !== null ? (
        <div className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 12 }}>
          {aside}
        </div>
      ) : null}
    </div>
    {children}
  </section>
);

PanelSection.displayName = 'PanelSection';

/**
 * How a row's value reads. Rows carry no icon: the value's own colour says whether the line is
 * settled ('default'), a gap worth acting on ('attention'), or a dead end nothing can fill
 * ('muted'), same three shades the rest of the bar already uses.
 */
export type PanelRowTone = 'default' | 'attention' | 'muted';

const TONE_TEXT: Record<PanelRowTone, string> = {
  default: 'text-white',
  attention: 'text-[#C09A5E]',
  muted: 'text-[#8E8E8E]',
};

interface PanelRowProps {
  label: string;
  value: React.ReactNode;
  tone?: PanelRowTone;
  /** Full text when the label is truncated to its readable head. */
  labelTitle?: string;
  /** A control that acts on this row only. Kept visually quiet: the row is the content. */
  action?: React.ReactNode;
  /** The value is being researched right now: it pulses like the chat's live line. */
  pulsing?: boolean;
  /** How old the value is ("checked 3 d ago"), shown faint under the label. */
  checked?: string;
}

export const PanelRow = ({ label, value, tone = 'default', labelTitle, action, pulsing, checked }: PanelRowProps) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <dt className="min-w-0 text-[#B4B4B4]" style={{ fontSize: 13 }} title={labelTitle}>
      {label}
      {checked ? (
        <span className="block text-[#8E8E8E]" style={{ fontSize: 11 }}>
          {checked}
        </span>
      ) : null}
    </dt>
    {/* The value never shrinks below its own text (a score read "23/…" once), it is only capped:
        past two thirds of the row it truncates and the label takes the rest. */}
    <dd
      className={`flex min-w-0 max-w-[65%] shrink-0 items-baseline gap-1.5 font-medium tabular-nums ${TONE_TEXT[tone]}`}
      style={{ fontSize: 13 }}
    >
      <span className={`truncate text-right${pulsing ? ' thinking-text-animated' : ''}`} style={{ fontSize: 13 }}>
        {value}
      </span>
      {action ? <span className="shrink-0">{action}</span> : null}
    </dd>
  </div>
);

PanelRow.displayName = 'PanelRow';
